import React, { useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  Typography,
  Box,
  useTheme,
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import { ColumnHeading } from "../../../components/Table";

import {
  CaseIcon as BaseCaseIcon,
  CustomerIcon as BaseCustomerIcon,
} from "../../../icons";
import { AccountSearchInfo, SERVER_PAGE_SIZE } from "../../../api/services";
import { formatAddress } from "../../../utils/formatAddress";
import { InlineType } from "../../../components/InlineType";
import { SearchCriteria } from "./SearchCriteria";
import { Highlight } from "../../../components/Highlight/Highlight";
import { AlertBlock } from "../../../components/AlertBlock";
import { CustomPagination } from "../../../components/CustomPagination";
import { AccountStatusChip } from "../../../components/AccountStatus";
import {
  CustomTableRow,
  CustomTableCell,
} from "../../../components/CustomTableComponents";

const CustomerIcon = styled(BaseCustomerIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  lineHeight: 1,
}));
const CaseIcon = styled(BaseCaseIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  lineHeight: 1,
}));

const ClickableTableRow = styled(CustomTableRow)(({ theme }) => ({
  cursor: "pointer",
  "&:focus": {
    background: fade(theme.palette.primary.main, 0.12),
  },
  "&:hover": {
    background: `${fade(theme.palette.primary.main, 0.04)} !important`,
  },
}));

//Proportion of the table width taken up by the column at different screen sizes
const accountNumberColumnProportions = [1 / 3, 1 / 5, 1 / 8, 1 / 8];
const customerColumnProportions = accountNumberColumnProportions.map(
  (num) => (1 - num) * 0.4,
);

interface Props {
  accounts: AccountSearchInfo[];
  onAccountSelected: (account: AccountSearchInfo) => void;
  selectedAccountNumber: string | null;
  onPageChanged: (id: number) => void;
  page: number;
  pageSize: number;
  resultsCount: number;
  isLastPage: boolean;
}

export const AccountSearchResults: React.FC<Props> = ({
  accounts,
  selectedAccountNumber,
  onPageChanged,
  onAccountSelected,
  page,
  pageSize,
  resultsCount: actualResultCount,
  isLastPage,
}) => {
  const theme = useTheme();

  const { resultsCount, hasMoreResults } = useMemo(() => {
    if (actualResultCount > SERVER_PAGE_SIZE) {
      return {
        resultsCount: SERVER_PAGE_SIZE,
        hasMoreResults: true,
      };
    } else {
      return { resultsCount: actualResultCount, hasMoreResults: false };
    }
  }, [actualResultCount]);

  const handleSelect = useCallback(
    (account: AccountSearchInfo) => (e: React.KeyboardEvent) => {
      if (e.which === 13) {
        e.preventDefault();
        onAccountSelected(account);
      }
    },
    [onAccountSelected],
  );

  return (
    <>
      <Typography variant="overline" data-testid="AccountSearchResults__Title">
        Search Results (
        {hasMoreResults ? `>${String(resultsCount)}` : String(resultsCount)})
      </Typography>
      <Box mt={2} clone>
        <Table tabIndex={0} data-testid="AccountSearchResults">
          <TableHead>
            <TableRow>
              <Box component={TableCell} width={accountNumberColumnProportions}>
                <ColumnHeading>Account</ColumnHeading>
              </Box>
              <Box component={TableCell} width={customerColumnProportions}>
                <ColumnHeading>Customer</ColumnHeading>
              </Box>
              <TableCell>
                <ColumnHeading>Billing Address</ColumnHeading>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((acct: AccountSearchInfo) => {
              const isCancelled = acct.status === "Cancelled";
              return (
                <ClickableTableRow
                  tabIndex={0}
                  hover
                  isGrey={isCancelled}
                  isStruck={isCancelled}
                  selected={acct.number === selectedAccountNumber}
                  key={acct.number}
                  data-testid="Table__Row"
                  onClick={() => onAccountSelected(acct)}
                  onKeyDown={handleSelect(acct)}
                >
                  <Box
                    component={CustomTableCell}
                    style={{ verticalAlign: "top" }}
                  >
                    <Box display="flex" flexWrap="no-wrap">
                      <Box flex="0 1 0">
                        <CaseIcon
                          fill={
                            isCancelled
                              ? theme.palette.text.secondary
                              : theme.palette.grey["900"]
                          }
                        />
                      </Box>
                      <Box flex="1 1 auto">
                        <Typography>
                          <InlineType
                            fontWeight={
                              acct.criteria.number !== null ? 700 : undefined
                            }
                            data-testid="Table__AccountNumber"
                          >
                            {acct.number}
                          </InlineType>
                        </Typography>
                        <Box mt={1}>
                          <AccountStatusChip
                            status={acct.status}
                            disableMargin
                          />
                        </Box>

                        <SearchCriteria
                          {...acct.criteria}
                          status={acct.status}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    component={CustomTableCell}
                    style={{ verticalAlign: "top" }}
                  >
                    <Box display="flex" flexWrap="no-wrap">
                      <Box flex="0 1 0">
                        <CustomerIcon
                          fill={
                            isCancelled
                              ? theme.palette.text.secondary
                              : theme.palette.grey["900"]
                          }
                        />
                      </Box>
                      <Box flex="1 1 auto">
                        <Typography>{acct.customerName}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    component={CustomTableCell}
                    style={{ verticalAlign: "top" }}
                  >
                    <Typography>
                      {acct.criteria.postcode ? (
                        <Highlight
                          phrase={acct.criteria.postcode}
                          element={InlineType}
                          elementProps={{
                            fontWeight: 700,
                            "data-testid": "Table__HighlightedPostcode",
                          }}
                        >
                          {formatAddress(acct.address)}
                        </Highlight>
                      ) : (
                        formatAddress(acct.address)
                      )}
                    </Typography>
                  </Box>
                </ClickableTableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      {hasMoreResults && isLastPage && (
        <Box my={2.5}>
          <AlertBlock
            variant="info"
            title="There are more results matching your search than we can currently show."
            messages={[
              "Please try a more specific search to get fewer results.",
            ]}
            alertProps={{
              "data-testid": "AccountSearchResults__AlertBlock--info",
            }}
          />
        </Box>
      )}
      <CustomPagination
        page={page}
        pageSize={pageSize}
        count={resultsCount}
        setPage={onPageChanged}
        my={5}
      />
    </>
  );
};
